import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Trash2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import ProjectNotes from '@/components/ProjectNotes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Operations = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [project, setProject] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Daily input form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    cutting_hours: '',
    edgeline_footage: '',
    centerline_footage: '',
    payroll: '',
    fuel: '',
    hotel: '',
    food: '',
    maintenance: '',
    rental: '',
    notes: '',
    is_job_complete: false,
    generate_invoice: false,
    invoice_date: new Date().toISOString().split('T')[0],
    invoice_number: ''
  });

  useEffect(() => {
    if (projectId) {
      fetchProjectData();
      fetchDailyData();
    }
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Failed to load project');
    }
  };

  const fetchDailyData = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/daily-data`, {
        headers: getAuthHeaders()
      });
      setDailyData(response.data);
    } catch (error) {
      console.error('Error fetching daily data:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const submitDailyData = async () => {
    try {
      // Calculate total feet cut from edgeline + centerline
      const totalFeetCut = (parseFloat(formData.edgeline_footage) || 0) + (parseFloat(formData.centerline_footage) || 0);
      
      // Send data with calculated feet_cut
      await axios.post(`${API}/projects/${projectId}/daily-data`, {
        ...formData,
        feet_cut: totalFeetCut
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Daily data saved successfully!');
      
      // If generate invoice was checked, download the invoice
      if (formData.generate_invoice) {
        try {
          const invoiceResponse = await axios.get(
            `${API}/projects/${projectId}/invoice`,
            {
              headers: getAuthHeaders(),
              responseType: 'blob'
            }
          );
          
          // Create download link
          const url = window.URL.createObjectURL(new Blob([invoiceResponse.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Invoice_${project.control_number}_${new Date().toISOString().split('T')[0]}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          
          toast.success('Invoice downloaded!');
        } catch (error) {
          console.error('Error downloading invoice:', error);
          toast.error('Failed to download invoice');
        }
      }
      
      setShowForm(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        cutting_hours: '',
        edgeline_footage: '',
        centerline_footage: '',
        payroll: '',
        fuel: '',
        hotel: '',
        food: '',
        maintenance: '',
        rental: '',
        notes: '',
        is_job_complete: false,
        generate_invoice: false,
        invoice_date: new Date().toISOString().split('T')[0],
        invoice_number: ''
      });
      fetchDailyData();
      fetchProjectData();
    } catch (error) {
      console.error('Error saving daily data:', error);
      toast.error('Failed to save daily data');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    // Parse the date string as a local date to avoid timezone shifts
    const [year, month, day] = dateString.split('T')[0].split('-');
    return new Date(year, month - 1, day).toLocaleDateString();
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/daily-data/${entryId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Daily entry deleted successfully');
      setDeleteConfirm(null);
      fetchDailyData();
      fetchProjectData();
    } catch (error) {
      console.error('Error deleting daily entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const calculateMetrics = () => {
    if (!project || !dailyData.length) return null;

    const totalFeetCut = dailyData.reduce((sum, day) => sum + (parseFloat(day.feet_cut) || 0), 0);
    const totalCosts = dailyData.reduce((sum, day) => 
      sum + (parseFloat(day.payroll) || 0) + 
            (parseFloat(day.fuel) || 0) + 
            (parseFloat(day.hotel) || 0) + 
            (parseFloat(day.food) || 0) + 
            (parseFloat(day.maintenance) || 0) + 
            (parseFloat(day.rental) || 0), 0);
    
    const daysWorked = dailyData.length;
    const avgLFPerDay = totalFeetCut / daysWorked;
    
    // Calculate target LF per day based on project's estimated days
    // This accounts for projects smaller than standard daily production rates
    const targetLFPerDay = project.total_lf / project.estimated_days;
    
    const actualCostPerLF = totalCosts / totalFeetCut;
    const budgetCostPerLF = project.estimated_cost / project.total_lf;
    const costVariance = ((actualCostPerLF - budgetCostPerLF) / budgetCostPerLF * 100);
    const productionEfficiency = (avgLFPerDay / targetLFPerDay * 100);
    
    const estimatedRevenue = project.bid_amount;
    const actualProfit = estimatedRevenue - totalCosts;
    const actualMargin = (actualProfit / estimatedRevenue * 100);
    const budgetMargin = project.estimated_margin;

    return {
      totalFeetCut,
      totalCosts,
      daysWorked,
      avgLFPerDay,
      targetLFPerDay,
      actualCostPerLF,
      budgetCostPerLF,
      costVariance,
      productionEfficiency,
      actualProfit,
      actualMargin,
      budgetMargin
    };
  };

  const metrics = calculateMetrics();

  if (!project) {
    return (
      <div className="relative min-h-screen">
        <div className="noise-overlay"></div>
        <div className="relative z-10 ml-64 p-8 flex items-center justify-center">
          <div className="text-[#94a3b8]">Loading project...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay"></div>
      
      <div className="relative z-10 ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-2">
                <span className="text-[#f97316]">Operations</span> Dashboard
              </h1>
              <p className="text-[#94a3b8] text-sm uppercase tracking-widest">
                {project.project_name || `${project.county} - ${project.highway}`}
              </p>
              <p className="text-sm mt-1">
                <span className="text-[#94a3b8]">Contractor: </span>
                <span className="font-semibold text-[#f97316]">{project.contractor_name || 'Unknown'}</span>
              </p>
            </div>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="millmetrix-button bg-[#10b981] hover:bg-[#059669] text-white"
              data-testid="add-daily-data-btn"
            >
              {showForm ? 'Cancel' : '+ Add Daily Data'}
            </Button>
          </div>

          {/* Daily Input Form */}
          {showForm && (
            <Card className="millmetrix-card p-6 mb-8">
              <h2 className="text-2xl font-bold tracking-tight mb-6 text-[#f8fafc]">Daily Production & Expense Entry</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="date" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Date
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className="millmetrix-input mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="cutting_hours" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Cutting Hours
                  </Label>
                  <Input
                    id="cutting_hours"
                    type="number"
                    step="0.1"
                    value={formData.cutting_hours}
                    onChange={(e) => handleInputChange('cutting_hours', e.target.value)}
                    className="millmetrix-input mt-2"
                    placeholder="0"
                  />
                </div>

                <div>
                  <Label htmlFor="edgeline_footage" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Edgeline Footage
                  </Label>
                  <Input
                    id="edgeline_footage"
                    type="number"
                    value={formData.edgeline_footage}
                    onChange={(e) => handleInputChange('edgeline_footage', e.target.value)}
                    className="millmetrix-input mt-2"
                    placeholder="0"
                  />
                </div>

                <div>
                  <Label htmlFor="centerline_footage" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Centerline Footage
                  </Label>
                  <Input
                    id="centerline_footage"
                    type="number"
                    value={formData.centerline_footage}
                    onChange={(e) => handleInputChange('centerline_footage', e.target.value)}
                    className="millmetrix-input mt-2"
                    placeholder="0"
                  />
                </div>

                <div>
                  <Label htmlFor="payroll" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Payroll ($)
                  </Label>
                  <Input
                    id="payroll"
                    type="number"
                    step="0.01"
                    value={formData.payroll}
                    onChange={(e) => handleInputChange('payroll', e.target.value)}
                    className="millmetrix-input mt-2"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="fuel" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Fuel ($)
                  </Label>
                  <Input
                    id="fuel"
                    type="number"
                    step="0.01"
                    value={formData.fuel}
                    onChange={(e) => handleInputChange('fuel', e.target.value)}
                    className="millmetrix-input mt-2"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="hotel" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Hotel ($)
                  </Label>
                  <Input
                    id="hotel"
                    type="number"
                    step="0.01"
                    value={formData.hotel}
                    onChange={(e) => handleInputChange('hotel', e.target.value)}
                    className="millmetrix-input mt-2"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="food" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Food ($)
                  </Label>
                  <Input
                    id="food"
                    type="number"
                    step="0.01"
                    value={formData.food}
                    onChange={(e) => handleInputChange('food', e.target.value)}
                    className="millmetrix-input mt-2"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="maintenance" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Maintenance ($)
                  </Label>
                  <Input
                    id="maintenance"
                    type="number"
                    step="0.01"
                    value={formData.maintenance}
                    onChange={(e) => handleInputChange('maintenance', e.target.value)}
                    className="millmetrix-input mt-2"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="rental" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Rental ($)
                  </Label>
                  <Input
                    id="rental"
                    type="number"
                    step="0.01"
                    value={formData.rental}
                    onChange={(e) => handleInputChange('rental', e.target.value)}
                    className="millmetrix-input mt-2"
                    placeholder="0.00"
                  />
                </div>

                <div className="lg:col-span-2">
                  <Label htmlFor="notes" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Notes
                  </Label>
                  <Input
                    id="notes"
                    type="text"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="millmetrix-input mt-2"
                    placeholder="Weather, issues, observations..."
                  />
                </div>

                <div className="flex items-center gap-3 bg-[#1e293b] p-4 rounded-md">
                  <input
                    id="job_complete"
                    type="checkbox"
                    checked={formData.is_job_complete}
                    onChange={(e) => handleInputChange('is_job_complete', e.target.checked)}
                    className="w-5 h-5 rounded border-[rgba(255,255,255,0.2)] bg-[#0f172a] checked:bg-[#f97316] checked:border-[#f97316] focus:ring-2 focus:ring-[#f97316] focus:ring-offset-0"
                  />
                  <Label htmlFor="job_complete" className="text-sm font-medium cursor-pointer text-[#f8fafc]">
                    Complete Job
                  </Label>
                </div>

                <div className="flex items-center gap-3 bg-[#0f172a] p-4 rounded-md border border-[#10b981]">
                  <input
                    id="generate_invoice"
                    type="checkbox"
                    checked={formData.generate_invoice}
                    onChange={(e) => handleInputChange('generate_invoice', e.target.checked)}
                    className="w-5 h-5 rounded border-[rgba(255,255,255,0.2)] bg-[#1e293b] checked:bg-[#10b981] checked:border-[#10b981] focus:ring-2 focus:ring-[#10b981] focus:ring-offset-0"
                  />
                  <Label htmlFor="generate_invoice" className="text-sm font-medium cursor-pointer text-[#f8fafc]">
                    Download Invoice PDF
                  </Label>
                </div>
              </div>

              {/* Invoice Details - shown when job is marked complete */}
              {formData.is_job_complete && (
                <div className="mt-4 p-4 bg-[#0f172a] rounded-lg border border-[#f97316]">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-[#f97316] mb-4">
                    Invoice Details (for Accounts Receivable)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="invoice_date" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        Invoice Date
                      </Label>
                      <Input
                        id="invoice_date"
                        type="date"
                        value={formData.invoice_date}
                        onChange={(e) => handleInputChange('invoice_date', e.target.value)}
                        className="millmetrix-input mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="invoice_number" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        Invoice Number (Optional)
                      </Label>
                      <Input
                        id="invoice_number"
                        type="text"
                        value={formData.invoice_number}
                        onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                        className="millmetrix-input mt-2"
                        placeholder="INV-2025-001"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end gap-3">
                <Button
                  onClick={() => setShowForm(false)}
                  variant="outline"
                  className="border-[#94a3b8] text-[#94a3b8]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitDailyData}
                  className="millmetrix-button bg-[#10b981] hover:bg-[#059669] text-white"
                  data-testid="save-daily-data-btn"
                >
                  Save Daily Data
                </Button>
              </div>
            </Card>
          )}

          {/* Live Six Sigma Metrics */}
          {metrics && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {/* Production Efficiency */}
                <Card className="millmetrix-card p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Production Efficiency
                    </div>
                    {metrics.productionEfficiency >= 100 ? (
                      <TrendingUp className="w-5 h-5 text-[#10b981]" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-[#f97316]" />
                    )}
                  </div>
                  <div className={`text-4xl font-mono font-bold tracking-tight tabular-nums ${
                    metrics.productionEfficiency >= 100 ? 'text-[#10b981]' : 'text-[#f97316]'
                  }`}>
                    {metrics.productionEfficiency.toFixed(1)}%
                  </div>
                  <div className="text-xs text-[#94a3b8] mt-1">
                    {Math.round(metrics.avgLFPerDay).toLocaleString()} LF/day vs {Math.round(metrics.targetLFPerDay).toLocaleString()} target
                  </div>
                </Card>

                {/* Cost Variance */}
                <Card className="millmetrix-card p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Cost Variance
                    </div>
                    {Math.abs(metrics.costVariance) > 10 ? (
                      <AlertTriangle className="w-5 h-5 text-[#f97316]" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-[#10b981]" />
                    )}
                  </div>
                  <div className={`text-4xl font-mono font-bold tracking-tight tabular-nums ${
                    metrics.costVariance > 0 ? 'text-[#f43f5e]' : 'text-[#10b981]'
                  }`}>
                    {metrics.costVariance > 0 ? '+' : ''}{metrics.costVariance.toFixed(1)}%
                  </div>
                  <div className="text-xs text-[#94a3b8] mt-1">
                    ${metrics.actualCostPerLF.toFixed(2)}/LF vs ${metrics.budgetCostPerLF.toFixed(2)} budget
                  </div>
                </Card>

                {/* Actual Margin */}
                <Card className="millmetrix-card p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Actual Margin
                    </div>
                    {metrics.actualMargin >= metrics.budgetMargin ? (
                      <TrendingUp className="w-5 h-5 text-[#10b981]" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-[#f97316]" />
                    )}
                  </div>
                  <div className={`text-4xl font-mono font-bold tracking-tight tabular-nums ${
                    metrics.actualMargin >= metrics.budgetMargin ? 'text-[#10b981]' : 'text-[#f97316]'
                  }`}>
                    {metrics.actualMargin.toFixed(1)}%
                  </div>
                  <div className="text-xs text-[#94a3b8] mt-1">
                    Target: {metrics.budgetMargin.toFixed(1)}%
                  </div>
                </Card>

                {/* Total Costs */}
                <Card className="millmetrix-card p-6">
                  <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                    Total Costs to Date
                  </div>
                  <div className="text-4xl font-mono font-bold tracking-tight text-[#0ea5e9] tabular-nums">
                    {formatCurrency(metrics.totalCosts)}
                  </div>
                  <div className="text-xs text-[#94a3b8] mt-1">
                    {metrics.daysWorked} days worked
                  </div>
                </Card>
              </div>

              {/* Detailed Metrics Table */}
              <Card className="millmetrix-card p-6">
                <h2 className="text-2xl font-bold tracking-tight mb-6 text-[#f8fafc]">Daily Production Log</h2>
                
                {dailyData.length === 0 ? (
                  <div className="text-center py-12 text-[#94a3b8]">
                    No daily data yet. Click "+ Add Daily Data" to start tracking!
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[rgba(255,255,255,0.1)]">
                          <th className="text-left p-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Date</th>
                          <th className="text-right p-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Feet Cut</th>
                          <th className="text-right p-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Hours</th>
                          <th className="text-right p-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Payroll</th>
                          <th className="text-right p-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Fuel</th>
                          <th className="text-right p-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Hotel</th>
                          <th className="text-right p-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Food</th>
                          <th className="text-right p-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Maintenance</th>
                          <th className="text-right p-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Rental</th>
                          <th className="text-right p-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Total Cost</th>
                          <th className="text-right p-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">$/LF</th>
                          <th className="text-center p-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dailyData.map((day, idx) => {
                          const dayTotal = (parseFloat(day.payroll) || 0) + (parseFloat(day.fuel) || 0) + 
                                         (parseFloat(day.hotel) || 0) + (parseFloat(day.food) || 0) + 
                                         (parseFloat(day.maintenance) || 0) + (parseFloat(day.rental) || 0);
                          const feetCut = parseFloat(day.feet_cut) || 0;
                          const costPerLF = feetCut > 0 ? (dayTotal / feetCut) : 0;
                          
                          return (
                            <tr key={idx} className="border-b border-[rgba(255,255,255,0.05)] hover:bg-[#1e293b]">
                              <td className="p-3 text-[#f8fafc]">{formatDate(day.date)}</td>
                              <td className="p-3 text-right font-mono text-[#f8fafc]">{parseInt(day.feet_cut || 0).toLocaleString()}</td>
                              <td className="p-3 text-right font-mono text-[#f8fafc]">{parseFloat(day.cutting_hours || 0).toFixed(1)}</td>
                              <td className="p-3 text-right font-mono text-[#10b981]">{formatCurrency(day.payroll)}</td>
                              <td className="p-3 text-right font-mono text-[#10b981]">{formatCurrency(day.fuel)}</td>
                              <td className="p-3 text-right font-mono text-[#10b981]">{formatCurrency(day.hotel)}</td>
                              <td className="p-3 text-right font-mono text-[#10b981]">{formatCurrency(day.food)}</td>
                              <td className="p-3 text-right font-mono text-[#10b981]">{formatCurrency(day.maintenance)}</td>
                              <td className="p-3 text-right font-mono text-[#10b981]">{formatCurrency(day.rental)}</td>
                              <td className="p-3 text-right font-mono font-bold text-[#10b981]">{formatCurrency(dayTotal)}</td>
                              <td className="p-3 text-right font-mono text-[#0ea5e9]">
                                {feetCut > 0 ? `$${costPerLF.toFixed(2)}` : '-'}
                              </td>
                              <td className="p-3 text-center">
                                <button
                                  onClick={() => setDeleteConfirm(day.id)}
                                  className="p-2 hover:bg-red-500/20 rounded-lg transition-colors group"
                                  title="Delete entry"
                                >
                                  <Trash2 className="w-4 h-4 text-[#94a3b8] group-hover:text-red-500" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {/* Project Notes/Activity Log */}
              <ProjectNotes 
                projectId={projectId} 
                projectName={project?.project_name || 'Project'} 
              />
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="millmetrix-card p-6 max-w-md w-full">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-red-500/20 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Delete Daily Entry?</h3>
                <p className="text-[#94a3b8]">
                  This action cannot be undone. The daily entry data will be permanently removed from the project.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                onClick={() => setDeleteConfirm(null)}
                variant="outline"
                className="border-[#94a3b8] text-[#94a3b8] hover:bg-[#1e293b]"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteEntry(deleteConfirm)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete Entry
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Operations;
